/**
 * Textos das exceções de carga (leve / normal / pesado) na Capacidade da
 * Fábrica e no painel do ambiente. Puro (sem React): as regras ficam em
 * `utils/capacidade/excecoes.js`; aqui só se decide como cada marca é dita.
 */
import {
  CLASSE,
  MOTIVO_CARGA,
  PESO_DA_CLASSE,
  SITUACAO,
  sugestaoDeClasse,
} from "../../utils/capacidade";
import { dataLocalDe, plural } from "../../utils/dinheiro";
import { inferTipoAmbiente, getTipoAmbienteSeguro } from "../../utils/tipoAmbiente";
import { ROTULO_CAMADA, ddmm } from "./apresentacao";

export const CLASSES_DA_MARCA = Object.freeze([
  { id: CLASSE.LEVE, rotulo: "Leve", explicacao: "metade de um ambiente comum" },
  { id: CLASSE.NORMAL, rotulo: "Normal", explicacao: "revisado, sem exceção" },
  { id: CLASSE.PESADO, rotulo: "Pesado", explicacao: "o dobro de um ambiente comum" },
]);

export const MOTIVOS_DA_MARCA = Object.freeze([
  { id: MOTIVO_CARGA.CURVA, rotulo: "Curva" },
  { id: MOTIVO_CARGA.ITEM_ESPECIAL, rotulo: "Item especial" },
  { id: MOTIVO_CARGA.VOLUME_GRANDE, rotulo: "Volume grande" },
  { id: MOTIVO_CARGA.OUTRO, rotulo: "Outro" },
]);

const ROTULO_DA_CLASSE = Object.fromEntries(CLASSES_DA_MARCA.map((c) => [c.id, c.rotulo]));
const ROTULO_DO_MOTIVO = Object.fromEntries(MOTIVOS_DA_MARCA.map((m) => [m.id, m.rotulo.toLowerCase()]));

const OPCOES_DATA_HORA = { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" };

const PESO_NA_TELA = (classe) => String(PESO_DA_CLASSE[classe]).replace(".", ",");

/** "Pesado · curva" / "Leve" (classe com inicial maiúscula, motivo em minúsculas). */
export function textoDaMarca(marca) {
  if (!marca) return "Sem marca";
  const classe = ROTULO_DA_CLASSE[marca.classe] || marca.classe;
  const motivo = ROTULO_DO_MOTIVO[marca.motivo];
  return motivo ? `${classe} · ${motivo}` : classe;
}

/** "conta como 2 ambientes" / "conta como meio ambiente". */
export function textoDoPeso(classe) {
  if (classe === CLASSE.LEVE) return "conta como meio ambiente";
  if (classe === CLASSE.PESADO) return `conta como ${PESO_NA_TELA(classe)} ambientes`;
  return "conta como 1 ambiente";
}

/** "por Fulano em 07/10/2026 14:03" (instante no fuso do aparelho). */
export function textoQuemQuando(marca) {
  const quando = dataLocalDe(marca?.marcado_em);
  const data = quando ? quando.toLocaleString("pt-BR", OPCOES_DATA_HORA).replace(",", "") : null;
  return [marca?.marcado_por ? `por ${marca.marcado_por}` : null, data ? `em ${data}` : null]
    .filter(Boolean)
    .join(" ");
}

/** Tipo do ambiente: o da planilha quando há, senão inferido pelo nome. */
export function tipoDoAmbiente(item, row) {
  return row ? getTipoAmbienteSeguro(row) : inferTipoAmbiente(item?.ambiente);
}

/** "leve" quando o tipo sugere (banheiro, lavabo, hall, área de serviço). */
export function sugestaoDoAmbiente(item, row) {
  return sugestaoDeClasse(tipoDoAmbiente(item, row));
}

/**
 * "ANA · entrega 30/06 · aprovado" — a segunda linha de cada ambiente. Na
 * lista de marcados há também os já terminados ("terminado 12/05").
 */
export function subtituloDoAmbiente(item) {
  if (item.situacao === SITUACAO.TERMINADO) {
    return [item.cliente || null, item.termino ? `terminado ${ddmm(item.termino)}` : "terminado"]
      .filter(Boolean)
      .join(" · ");
  }
  return [
    item.cliente || null,
    item.entrega ? `entrega ${ddmm(item.entrega)}` : "sem entrega",
    ROTULO_CAMADA[item.camada] ? ROTULO_CAMADA[item.camada].toLowerCase() : null,
  ]
    .filter(Boolean)
    .join(" · ");
}

/** Mensagem de sucesso do lote: "3 ambientes marcados como leve." */
export function textoDoSucesso(quantidade, classe) {
  return `${plural(quantidade, "ambiente marcado", "ambientes marcados")} como ${String(
    ROTULO_DA_CLASSE[classe] || classe,
  ).toLowerCase()}.`;
}
